// src/lib/cache/clients.js
//
// Per-entity cache wiring for clients. Pattern mirrors calendarEvents.js.
// Mutations are optimistic. The clients table has no rejecting INSERT trigger
// (only an UPDATE trigger that touches updated_at) and no server-generated
// numbered columns. The JS-layer check_restriction RPC in _createClient CAN
// throw when a paid-plan client limit is hit; that's an ordinary error path
// and the optimistic factory rolls back via onError.

import {
  getClients,
  createClient,
  updateClient,
  deleteClient,
} from '../api/core/clients.js'
import {
  useEntityListQuery,
  useOptimisticListMutation,
  useStandardDeleteMutation,
  defaultCreateReconcile,
  defaultUpdateReconcile,
  unwrapList,
} from './entityCache.js'

const ENTITY = 'clients'

// Mirror the DB defaults applied at insert time so the placeholder row renders
// the same way a freshly-fetched row would. The full server row replaces this
// in onSuccess (whole-row replace by tempId, not a merge).
const buildCreatePlaceholder = (vars, tempId) => {
  const now = new Date().toISOString()
  return {
    status: 'active',
    automation_enabled: true,
    sms_consent: false,
    marketing_sms_consent: false,
    marketing_email_consent: false,
    address: '{}',
    ...vars,
    id: tempId,
    created_at: now,
    updated_at: now,
  }
}

export const useClientsQuery = () =>
  useEntityListQuery(ENTITY, unwrapList('clients', getClients))

export const useCreateClientMutation = () =>
  useOptimisticListMutation(ENTITY, {
    mutationFn: (clientData) => createClient(clientData),
    applyOptimistic: (prev, vars, { tempId }) => [
      buildCreatePlaceholder(vars, tempId),
      ...prev,
    ],
    reconcile: defaultCreateReconcile,
  })

export const useUpdateClientMutation = () =>
  useOptimisticListMutation(ENTITY, {
    mutationFn: ({ id, ...patch }) => updateClient(id, patch),
    applyOptimistic: (prev, vars) =>
      prev.map((row) => (row.id === vars.id ? { ...row, ...vars } : row)),
    reconcile: defaultUpdateReconcile,
  })

export const useDeleteClientMutation = () =>
  useStandardDeleteMutation(ENTITY, deleteClient)
